import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { environment } from 'environments/environment';

import {
	CheckListingStateResponseModel,
	GetAccessoryListingsLevelOneRequestModel,
	GetAccessoryListingsLevelOneResponseModel,
	GetListingDetailsResponseModel,
	GetListingsLevelOneFilterOptionsModel,
	GetWineListingsLevelOneRequestModel,
	GetWineListingsLevelOneResponseModel,
	GetWineListingsLevelTwoRequestModel,
	GetWineListingsLevelTwoResponseModel,
} from '../models/view-listings.models';

@Injectable({
	providedIn: 'root',
})
export class ViewListingsService {
	private searchEvent = new Subject<any>();

	searchEvent$ = this.searchEvent.asObservable();

	private get baseApiUrl(): string {
		return environment.marketplaceUrl;
	}

	constructor(private http: HttpClient) {}

	// Wine
	getListingLevelOneV1(
		searchModel: GetWineListingsLevelOneRequestModel,
	): Observable<GetWineListingsLevelOneResponseModel> {
		const url = `${this.baseApiUrl}/v1/ViewListings/LevelOne`;
		let params = new HttpParams()
			.append('filterText', searchModel.filterText)
			.append('orderByColumn', searchModel.orderByColumn)
			.append('orderByDirection', searchModel.orderByDirection)
			.append('page', searchModel.page.toString())
			.append('pageSize', searchModel.pageSize.toString())
			.append('categoryUrlSlug', searchModel.categoryUrlSlug);

		searchModel.sites?.forEach((siteId) => {
			params = params.append('sites', siteId);
		});
		searchModel.vintages?.forEach((vintage) => {
			params = params.append('vintages', vintage);
		});
		searchModel.wineTypes?.forEach((wineType) => {
			params = params.append('wineTypes', wineType);
		});
		searchModel.styles?.forEach((style) => {
			params = params.append('styles', style);
		});
		searchModel.varieties?.forEach((variety) => {
			params = params.append('varieties', variety);
		});
		searchModel.regions?.forEach((region) => {
			params = params.append('regions', region);
		});
		searchModel.producers?.forEach((producer) => {
			params = params.append('producers', producer);
		});
		searchModel.countries?.forEach((country) => {
			params = params.append('countries', country);
		});
		searchModel.price?.forEach((priceRange) => {
			params = params.append('minPrices', priceRange[0]);
			params = params.append('maxPrices', priceRange[1]);
		});
		searchModel.provenance?.forEach((status) => {
			params = params.append('provenance', status);
		});
		if (searchModel.categories?.length > 0) {
			// Filter component is currently set up to only allow for one category to be selected so we can just use index [0]
			params = params.append('categoryId', searchModel?.categories[0]);
		}
		searchModel.size?.forEach((size) => {
			params = params.append('bottleSizes', size);
		});
		searchModel.wineName?.forEach((wineNameId) => {
			params = params.append('wineName', wineNameId);
		});

		return this.http.get<GetWineListingsLevelOneResponseModel>(url, { params });
	}

	getListingFiltersLevelOneV1(
		searchModel: GetWineListingsLevelOneRequestModel,
	): Observable<GetListingsLevelOneFilterOptionsModel> {
		const url = `${this.baseApiUrl}/v1/ViewListings/filterOptions`;
		let params = new HttpParams()
			.append('filterText', searchModel.filterText)
			.append('categoryUrlSlug', searchModel.categoryUrlSlug)
			.append('loadAccessories', searchModel.loadAccessories);

		searchModel.sites?.forEach((siteId) => {
			params = params.append('sites', siteId);
		});
		searchModel.vintages?.forEach((vintage) => {
			params = params.append('vintages', vintage);
		});
		searchModel.wineTypes?.forEach((wineType) => {
			params = params.append('wineTypes', wineType);
		});
		searchModel.styles?.forEach((style) => {
			params = params.append('styles', style);
		});
		searchModel.varieties?.forEach((variety) => {
			params = params.append('varieties', variety);
		});
		searchModel.regions?.forEach((region) => {
			params = params.append('regions', region);
		});
		searchModel.producers?.forEach((producer) => {
			params = params.append('producers', producer);
		});
		searchModel.countries?.forEach((country) => {
			params = params.append('countries', country);
		});
		searchModel.price?.forEach((priceRange) => {
			params = params.append('minPrices', priceRange[0]);
			params = params.append('maxPrices', priceRange[1]);
		});
		searchModel.provenance?.forEach((status) => {
			params = params.append('provenance', status);
		});
		if (searchModel.categories?.length > 0) {
			// Filter component is currently set up to only allow for one category to be selected so we can just use index [0]
			params = params.append('categoryId', searchModel?.categories[0]);
		}
		searchModel.size?.forEach((size) => {
			params = params.append('bottleSizes', size);
		});
		return this.http.get<GetListingsLevelOneFilterOptionsModel>(url, {
			params,
		});
	}

	getListingLevelTwoV1(
		searchModel: GetWineListingsLevelTwoRequestModel,
	): Observable<GetWineListingsLevelTwoResponseModel> {
		const url = `${this.baseApiUrl}/v1/ViewListings/LevelTwo`;
		let params = new HttpParams()
			.append('filterText', searchModel.filterText)
			.append('orderByColumn', searchModel.orderByColumn)
			.append('orderByDirection', searchModel.orderByDirection)
			.append('page', searchModel.page.toString())
			.append('pageSize', searchModel.pageSize.toString())
			.append('groupId', searchModel.groupId);

		if (searchModel.categoryUrl) {
			params = params.append('categoryUrl', searchModel.categoryUrl);
		}
		searchModel.sites?.forEach((siteId) => {
			params = params.append('sites', siteId);
		});
		searchModel['Condition: Ullage']?.forEach((condition) => {
			params = params.append('ullageCondition', condition);
		});
		searchModel['Condition: Label']?.forEach((condition) => {
			params = params.append('labelCondition', condition);
		});
		searchModel['Condition: capsule']?.forEach((condition) => {
			params = params.append('capsuleCondition', condition);
		});
		searchModel.sizes?.forEach((sizeId) => {
			params = params.append('sizes', sizeId);
		});
		searchModel.price?.forEach((priceRange) => {
			params = params.append('minPrices', priceRange[0]);
			params = params.append('maxPrices', priceRange[1]);
		});
		searchModel.provenance?.forEach((status) => {
			params = params.append('provenance', status);
		});

		return this.http.get<GetWineListingsLevelTwoResponseModel>(url, { params });
	}

	updateListingSearchGroupItems(groupId: string): Observable<void> {
		const url = `${this.baseApiUrl}/v1/listingGroup/${groupId}`;
		return this.http.put<void>(url, {});
	}

	getListingDetailsV1(id: number, key: string): Observable<GetListingDetailsResponseModel> {
		let url = `${this.baseApiUrl}/v1/ViewListings/${id}`;
		if (key) {
			url = url + `/${key}`;
		}
		return this.http.get<GetListingDetailsResponseModel>(url, {});
	}

	getListingStatusForSearchV1(id: number): Observable<CheckListingStateResponseModel> {
		const url = `${this.baseApiUrl}/v1/ViewListings/checkListingId/${id}`;
		return this.http.get<CheckListingStateResponseModel>(url, {});
	}

	// accessories
	getAccessoryListingLevelOneV1(
		searchModel: GetAccessoryListingsLevelOneRequestModel,
	): Observable<GetAccessoryListingsLevelOneResponseModel> {
		const url = `${this.baseApiUrl}/v1/ViewListings/Accessories/LevelOne`;
		let params = new HttpParams()
			.append('filterText', searchModel.filterText)
			.append('orderByColumn', searchModel.orderByColumn)
			.append('orderByDirection', searchModel.orderByDirection)
			.append('page', searchModel.page.toString())
			.append('pageSize', searchModel.pageSize.toString())
			.append('loadAccessories', true);

		if (searchModel.categoryUrlSlug) {
			params = params.append('categoryUrlSlug', searchModel.categoryUrlSlug);
		}

		searchModel.sites?.forEach((siteId) => {
			params = params.append('sites', siteId);
		});
		searchModel.wineTypes?.forEach((wineType) => {
			params = params.append('wineTypes', wineType);
		});
		searchModel.varieties?.forEach((variety) => {
			params = params.append('varieties', variety);
		});
		searchModel.producers?.forEach((producer) => {
			params = params.append('producers', producer);
		});
		searchModel.price?.forEach((priceRange) => {
			params = params.append('minPrices', priceRange[0]);
			params = params.append('maxPrices', priceRange[1]);
		});
		if (searchModel.categories?.length > 0) {
			// Filter component is currently set up to only allow for one category to be selected so we can just use index [0]
			params = params.append('categoryId', searchModel?.categories[0]);
		}
		return this.http.get<GetAccessoryListingsLevelOneResponseModel>(url, {
			params,
		});
	}

	generateNumberArray(count: number): number[] {
		return Array.from({ length: Math.min(count, 50) }, (_, index) => index + 1);
	}
}
